import { Plan } from '@/classes/stripe';
import type { PlanState } from './StripeSubscriptionService';

type SubscribeButtonLabel = 'continue' | 'selected' | 'upgrade' | 'downgrade' | 'reactivate' | 'incomplete';

const labels: { [key in PlanState]: UiLabel } = {
  NO_PLAN: {
    label: 'fields.pricing_table.button.checkout_now',
    state: 'continue',
  },
  PLAN_INCOMPLETE: {
    label: 'fields.pricing_table.button.incomplete',
    state: 'incomplete',
  },
  PLAN_ACTIVE: {
    label: 'fields.pricing_table.button.active',
    state: 'selected',
  },
  PLAN_UPCOMING: {
    label: 'fields.pricing_table.button.upcoming',
    state: 'selected',
  },
  PLAN_INFERIOR: {
    label: 'fields.pricing_table.button.downgrade',
    state: 'downgrade',
  },
  PLAN_DOWNGRADING: {
    label: 'fields.pricing_table.button.reactivate',
    state: 'reactivate',
  },
  PLAN_CANCELLED: {
    label: 'fields.pricing_table.button.reactivate',
    state: 'reactivate',
  },
  PLAN_SUPERIOR: {
    label: 'fields.pricing_table.button.upgrade',
    state: 'upgrade',
  },
} as const;

type UiLabel = {
  label: string;
  state: SubscribeButtonLabel;
};

export function labelForPlanState(planState: PlanState): UiLabel {
  return labels[planState] ?? labels.NO_PLAN;
}

// Order the plans descending by plan level
export function filterAndSortPlans(subscriptionPlans: Plan[]): Plan[] {
  return subscriptionPlans
    .filter(
      (plan: Plan) =>
        !plan.hidden &&
        (plan.interval === 'year' || plan.interval === 'day' || plan.interval === 'month') &&
        !plan.isEnterprisePlan
    )
    .sort((planA: Plan, planB: Plan) => parseInt(planB.planLevel as string) - parseInt(planA.planLevel as string));
}

// Translation key of a plan's title. The legacy Flex product's translationKey reads "Starter Flex", so Starter
// monthly gets its "Starter Monthly" title here; every other plan uses its product's translationKey.
export function planTitleKey(plan?: Plan | null): string {
  return plan?.planName === 'Flex' ? 'fields.pricing_table.flex_plan_title_monthly' : `fields.${plan?.translationKey}`;
}

// How much cheaper per month the annual plan is compared to its monthly variant, rounded down.
export function annualSavingsPercent(annualPlan: Plan, monthlyPlan: Plan): number {
  const monthlyPrice = monthlyPlan.price.priceToInt();
  if (!monthlyPrice) return 0;

  // The epsilon keeps floating-point error from flooring an exact percentage one point down (e.g. 19.999… → 19).
  const savings = Math.floor((1 - annualPlan.price.priceToInt() / 12 / monthlyPrice) * 100 + 1e-9);

  return Math.max(savings, 0);
}
